import { memo, useState, useRef, useCallback } from "react";
import { useI18n } from "@/i18n";
import { sendContactEmail } from "@/infrastructure/email/emailService";
import { colors, typography, spacing, transitions } from "@/design-system/tokens";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITEKEY = "6LdQL54rAAAAAN-OJNNzLgSZNUsMlunhfMys3tZv";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Pricing = memo(function Pricing() {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const captchaRef = useRef<ReCAPTCHA>(null);

  const isValid = name.trim().length > 0 && EMAIL_REGEX.test(email) && message.trim().length > 0 && !!recaptchaToken;

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!recaptchaToken) { setError("Por favor, verifica que no eres un robot."); return; }
    if (!name.trim() || !email.trim() || !message.trim()) { setError("Por favor, completa todos los campos."); return; }
    if (!EMAIL_REGEX.test(email)) { setError("Por favor, ingresa un email válido."); return; }

    setSubmitting(true);
    const result = await sendContactEmail({
      name: name.trim(),
      email: email.trim(),
      message: message.trim(),
      subject: `[Nexo Warehouse] Nuevo mensaje de contacto de ${name.trim()}`,
      recaptchaToken,
    });

    if (result.success) {
      setSuccess(t.contact.successMessage);
      setName("");
      setEmail("");
      setMessage("");
      setRecaptchaToken(null);
      captchaRef.current?.reset();
    } else {
      setError(result.message ?? "Error");
    }
    setSubmitting(false);
  }, [name, email, message, recaptchaToken, t]);

  return (
    <section id="pricing" style={{ background: "#f7f7f7", padding: `${spacing["3xl"]} ${spacing.xl}` }}>
      <div style={{ maxWidth: "520px", margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "36px" }}>
        <div style={{ textAlign: "center" }}>
          <h2 style={{ fontFamily: typography.fontFamily, fontSize: "2.2rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.pricing.title}</h2>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, color: "#888", marginTop: "10px" }}>{t.pricing.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", gap: "18px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>{t.contact.nameLabel} *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder={t.contact.namePlaceholder} maxLength={100} style={inputStyle} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>{t.contact.emailLabel} *</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder={t.contact.emailPlaceholder} maxLength={255} style={inputStyle} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>{t.contact.messageLabel} *</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder={t.contact.messagePlaceholder} maxLength={1000} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <ReCAPTCHA ref={captchaRef} sitekey={RECAPTCHA_SITEKEY} onChange={(token) => setRecaptchaToken(token)} theme="light" />
          </div>

          {error && (
            <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#dc2626", background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: "8px", padding: "10px 14px", textAlign: "center" }}>{error}</div>
          )}
          {success && (
            <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#16a34a", background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: "8px", padding: "10px 14px", textAlign: "center" }}>{success}</div>
          )}

          <button
            type="submit"
            disabled={!isValid || submitting}
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.base,
              fontWeight: typography.weight.bold,
              color: "#fff",
              background: isValid && !submitting ? colors.primary : "#ccc",
              border: "none",
              borderRadius: "12px",
              padding: "14px 24px",
              cursor: isValid && !submitting ? "pointer" : "not-allowed",
              transition: `background ${transitions.fast}`,
              width: "100%",
            }}
            onMouseEnter={e => { if (isValid && !submitting) (e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover; }}
            onMouseLeave={e => { if (isValid && !submitting) (e.currentTarget as HTMLButtonElement).style.background = colors.primary; }}
          >
            {submitting ? "Enviando..." : t.contact.submit}
          </button>
        </form>
      </div>
    </section>
  );
});

const labelStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" };
const inputStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: "10px", padding: "12px 14px", outline: "none", width: "100%", boxSizing: "border-box" };
